<script setup>
// --------------------------------- Imports -------------------------------- //
import { inject } from 'vue';
import { useBiStore } from '~/bi/store/bi.store';
import { hasFilterWithSameName } from '~/bi/utils/bi-filter-helper.utils';
import { useCommonImports } from '~/common/composables/common-imports.composable.js';

const {
  $t,
  $toast,
} = useCommonImports();
const draggable = defineAsyncComponent(() => import('vuedraggable'));

// ---------------------------------- Props --------------------------------- //

// ---------------------------------- Emits --------------------------------- //

// ---------------------------- Injects/Provides ---------------------------- //
const global_filters_of_component = inject('global_filters_of_component');

// ------------------- Variables - Local - consts and lets ------------------ //
const bi_store = useBiStore();

// ------------------------ Variables - Local - refs ------------------------ //

// ---------------------- Variables - Local - reactives --------------------- //

// --------------------------- Computed properties -------------------------- //
const has_filter_with_no_name = computed(() => global_filters_of_component.value.some(filter => !filter.general?.filter_name));

// -------------------------------- Functions ------------------------------- //
function addFilter() {
  if (hasFilterWithSameName(global_filters_of_component.value, bi_store.pre_defined_filter_name)) {
    const translated_text = `${$t('A filter with the same name')} "${$t('New Filter')}" ${$t('already exists')}`;
    $toast({ text: translated_text, type: 'error' });
    return;
  }
  const pre_defined_filter = {
    uid: crypto.randomUUID(),
    general: {
      filter_name: bi_store.pre_defined_filter_name,
      filter_type: 'text',
      dataset: null,
      column: null,
      set_default_filter_value: false,
      default_filter_config: {},
    },
    widgets: {},
  };

  global_filters_of_component.value.push(pre_defined_filter);
  bi_store.selected_global_filter_uid = pre_defined_filter.uid;
}
function removeFilter(filter_uid) {
  const filter_index = global_filters_of_component.value.findIndex(filter => filter.uid === filter_uid);
  if (filter_index !== -1) {
    global_filters_of_component.value.splice(filter_index, 1);

    if (global_filters_of_component.value.length) {
      bi_store.selected_global_filter_uid = global_filters_of_component.value[0].uid;
    }
  }
}
function onReorder() {
  // console.log('onReorder');
}
function onFilterSelect(field) {
  bi_store.selected_global_filter_uid = field.uid;
}
// -------------------------------- Watchers -------------------------------- //

// ----------------------------- Lifecycle Hooks ---------------------------- //
onMounted(() => {
});
</script>

<template>
  <div class="border border-gray-300 bg-gray-100 rounded-md w-1/2" :class="{ 'pointer-events-none': has_filter_with_no_name }">
    <div class="flex flex-col gap-2">
      <div v-if="global_filters_of_component.length" class="scrollbar h-[330px]">
        <draggable v-model="global_filters_of_component" item-key="uid" tag="div" handle=".move" :animation="150" @change="onReorder">
          <template #item="{ element: filter, index }">
            <div
              class="group p-2 rounded-md mx-2 my-1 cursor-pointer"
              :class="{ 'bg-gray-700': bi_store.selected_global_filter_uid === filter.uid, 'hover:bg-gray-200': bi_store.selected_global_filter_uid !== filter.uid }"
              @click="onFilterSelect(filter, index)"
            >
              <div class="flex items-center justify-between">
                <div class="flex items-center">
                  <div class="pr-3 move cursor-move invisible group-hover:visible">
                    <IconHawkDragIcon class="text-gray-400 w-[12px] h-[12px]" :class="{ 'text-white ': bi_store.selected_global_filter_uid === filter.uid }" />
                  </div>
                  <HawkText :length="30" class="text-sm text-gray-700" :class="{ 'text-white font-semibold': bi_store.selected_global_filter_uid === filter.uid }" :content="filter.general?.filter_name" />
                  <IconHawkWarningSign v-if="!Object.keys(filter?.widgets || {}).length > 0" class="text-warning-500 w-[18px] h-[18px] ml-2" />
                </div>
                <div class="text-gray-500 hidden group-hover:block mr-2">
                  <IconHawkTrashThree class="text-gray-500 w-[18px] h-[18px]" :class="{ 'text-white ': bi_store.selected_global_filter_uid === filter.uid }" @click.stop="removeFilter(filter.uid)" />
                </div>
              </div>
            </div>
          </template>
        </draggable>
      </div>
      <HawkButton type="link" class="ml-2" color="gray" @click="addFilter">
        <IconHawkPlus />
        <span>
          {{ $t('Add filter') }}
        </span>
      </HawkButton>
    </div>
  </div>
</template>
